import { useEffect, useMemo, useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, Filter, RotateCcw, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

const parseDT = (s) => {
  const m = /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(s || ""));
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
};

const sortVal = (col, r) => {
  const t = col.token(r);
  if (col.type === "num") return Number(t);
  if (col.type === "datetime") return parseDT(t);
  return t;
};

const cmpTokens = (col, a, b) => {
  if (col.type === "num") return Number(a) - Number(b);
  if (col.type === "datetime") {
    const pa = parseDT(a), pb = parseDT(b);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return pa - pb;
  }
  return String(a).localeCompare(String(b), "id", { numeric: true });
};

const label = (col, t) => (col.tokenLabel ? col.tokenLabel(t) : t === "" || t == null ? "(Kosong)" : String(t));

function HeaderCell({ col, rows, otherPass, filters, setFilters, sort, setSort }) {
  const [q, setQ] = useState("");
  const base = useMemo(() => rows.filter(otherPass), [rows, otherPass]);
  const distinct = useMemo(() => {
    const set = new Set();
    base.forEach((r) => set.add(col.token(r)));
    return [...set].sort((a, b) => cmpTokens(col, a, b));
  }, [base, col]);
  const sel = filters[col.key];
  const isChecked = (t) => !sel || sel.has(t);
  const visible = distinct.filter((t) => label(col, t).toLowerCase().includes(q.toLowerCase()));
  const allVisible = visible.length > 0 && visible.every(isChecked);
  const colActive = Boolean(sel) || sort?.key === col.key;

  const setSel = (mutate) =>
    setFilters((prev) => {
      const cur = new Set(prev[col.key] ?? distinct);
      mutate(cur);
      const next = { ...prev };
      if (cur.size >= distinct.length && distinct.every((t) => cur.has(t))) delete next[col.key];
      else next[col.key] = cur;
      return next;
    });
  const toggle = (t) => setSel((cur) => { cur.has(t) ? cur.delete(t) : cur.add(t); });
  const toggleAll = () => setSel((cur) => visible.forEach((t) => (allVisible ? cur.delete(t) : cur.add(t))));
  const clear = () => {
    setFilters((p) => { const n = { ...p }; delete n[col.key]; return n; });
    if (sort?.key === col.key) setSort(null);
    setQ("");
  };
  const sortBtn = (dir, Icon, text) => (
    <button
      onClick={() => setSort({ key: col.key, dir })}
      data-testid={`sort-${dir}-${col.key}`}
      className={cn("press flex flex-1 items-center justify-center gap-1 rounded-md border py-1.5 text-xs font-semibold",
        sort?.key === col.key && sort.dir === dir ? "border-ink bg-ink text-white" : "border-zinc-300 hover:border-ink")}
    >
      <Icon className="h-3.5 w-3.5" /> {text}
    </button>
  );

  return (
    <th className={cn(col.align === "right" && "text-right")}>
      <div className={cn("flex items-center gap-1", col.align === "right" && "justify-end")}>
        <span>{col.label}</span>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid={`filter-${col.key}`} aria-label={`Filter ${col.label}`}
              className={cn("press grid h-5 w-5 shrink-0 place-items-center rounded hover:bg-white/20", colActive ? "text-brand" : "text-white/55")}>
              <Filter className="h-3.5 w-3.5" fill={colActive ? "currentColor" : "none"} />
            </button>
          </PopoverTrigger>
          <PopoverContent align={col.align === "right" ? "end" : "start"} className="w-64 p-0 text-ink" data-testid={`filter-panel-${col.key}`}>
            <div className="flex gap-1 border-b p-2">
              {sortBtn("asc", ArrowUpAZ, "A→Z")}
              {sortBtn("desc", ArrowDownAZ, "Z→A")}
            </div>
            <div className="relative border-b p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nilai…" aria-label={`Cari nilai ${col.label}`}
                className="h-8 w-full rounded-md border border-zinc-300 pl-8 pr-2 text-xs outline-none focus:border-ink" data-testid={`filter-search-${col.key}`} />
            </div>
            <label className="flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-xs font-semibold">
              <Checkbox checked={allVisible} onCheckedChange={toggleAll} data-testid={`filter-all-${col.key}`} /> Pilih semua
            </label>
            <div className="max-h-52 overflow-auto py-1">
              {visible.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">Tidak ada nilai</div>}
              {visible.map((t) => (
                <label key={String(t)} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50">
                  <Checkbox checked={isChecked(t)} onCheckedChange={() => toggle(t)} />
                  <span className="truncate" title={label(col, t)}>{label(col, t)}</span>
                </label>
              ))}
            </div>
            <div className="border-t p-2">
              <button onClick={clear} data-testid={`filter-clear-${col.key}`}
                className="press flex w-full items-center justify-center gap-1 rounded-md border border-zinc-300 py-1.5 text-xs font-semibold hover:border-ink">
                <X className="h-3.5 w-3.5" /> Bersihkan
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </th>
  );
}

export default function FilterTable({ columns, rows, testid, extraFilter, onCount }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);
  const inScope = (r) => !extraFilter || extraFilter(r);
  const pass = (r, exceptKey) =>
    columns.every((c) => {
      if (c.key === exceptKey) return true;
      const sel = filters[c.key];
      return !sel || sel.has(c.token(r));
    });

  const filtered = useMemo(() => {
    let out = rows.filter((r) => inScope(r) && pass(r));
    if (sort) {
      const c = columns.find((x) => x.key === sort.key);
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((ra, rb) => {
        const a = sortVal(c, ra), b = sortVal(c, rb);
        const ae = a === null || a === "" || a === undefined || (c.type === "num" && Number.isNaN(a));
        const be = b === null || b === "" || b === undefined || (c.type === "num" && Number.isNaN(b));
        if (ae && be) return 0;
        if (ae) return 1;
        if (be) return -1;
        if (c.type === "num" || c.type === "datetime") return (a - b) * dir;
        return String(a).localeCompare(String(b), "id", { numeric: true }) * dir;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters, sort, columns, extraFilter]);

  useEffect(() => { onCount?.(filtered.length); }, [filtered.length, onCount]);

  const hasActive = Object.keys(filters).length > 0 || sort;
  return (
    <div data-testid={testid}>
      <div className="mb-2 flex items-center justify-between gap-2 text-xs text-zinc-500">
        <span data-testid={`${testid}-count`}>Menampilkan <b className="text-ink">{filtered.length}</b> dari {rows.length} baris</span>
        {hasActive && (
          <button onClick={() => { setFilters({}); setSort(null); }} data-testid={`${testid}-reset`}
            className="press inline-flex items-center gap-1 rounded-md border border-zinc-300 px-2 py-1 font-semibold text-ink hover:border-ink">
            <RotateCcw className="h-3 w-3" /> Reset semua filter
          </button>
        )}
      </div>
      <div className="max-h-[480px] overflow-auto rounded-xl border border-zinc-200 bg-white">
        <table className="dense-table w-full min-w-[640px]">
          <thead>
            <tr>
              {columns.map((c) => (
                <HeaderCell key={c.key} col={c} rows={rows.filter(inScope)} otherPass={(r) => pass(r, c.key)}
                  filters={filters} setFilters={setFilters} sort={sort} setSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className={cn(c.align === "right" && "text-right num", c.cellClass)}>
                    {c.cell ? c.cell(r) : label(c, c.token(r))}
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length} className="py-8 text-center text-zinc-400">Tidak ada baris sesuai filter</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
